import { MultiSelect } from "react-multi-select-component";
import Chip from "./Chip";

/**
 * Unified multi-select dropdown component
 *
 * @param {string} label - Label text above the dropdown
 * @param {Array} options - Array of { label, value } options
 * @param {Array} value - Currently selected options
 * @param {function} onChange - Called with new selection array
 * @param {string} placeholder - Placeholder when nothing selected
 * @param {boolean} showChips - Whether to show selected items as removable chips
 * @param {function} chipLabelFn - Optional function to customize chip label (receives option)
 * @param {function} valueRendererFn - Optional custom value renderer
 * @param {function} itemRendererFn - Optional custom item renderer
 * @param {string} className - Additional classes for container
 */
const MultiSelectDropdown = ({
  label,
  options,
  value = [],
  onChange,
  placeholder = "Select...",
  showChips = true,
  disableSearch = false,
  chipLabelFn,
  valueRendererFn,
  itemRendererFn,
  className = "",
}) => {
  // Ensure value is always an array
  const safeValue = Array.isArray(value) ? value : [];
  // Default value renderer - shows count when multiple selected
  const defaultValueRenderer = selected => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) {
      const lbl = selected[0].label;
      // If label has emoji:name format, show just the name
      return lbl.includes(":") ? lbl.split(":")[1].trim() : lbl;
    }
    return `${selected.length} selected`;
  };

  // Default chip label - show value or short label
  const defaultChipLabel = option => {
    if (Array.isArray(option.value)) {
      // Region group - show full label
      return option.label;
    }
    // Single item - show value (e.g., "US") or short label
    return option.value;
  };

  const handleRemove = optionValue => {
    onChange(safeValue.filter(v => v.value !== optionValue));
  };

  const getChipLabel = chipLabelFn || defaultChipLabel;
  const getValueRenderer = valueRendererFn || defaultValueRenderer;

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium uppercase tracking-wider text-black/40 mb-2">
          {label}
        </label>
      )}

      <MultiSelect
        options={options}
        value={safeValue}
        onChange={onChange}
        labelledBy={placeholder}
        isCreatable={false}
        hasSelectAll={false}
        disableSearch={disableSearch}
        className="multi-select-custom text-sm"
        valueRenderer={getValueRenderer}
        ItemRenderer={itemRendererFn}
      />

      {/* Selected chips */}
      {showChips && safeValue.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 justify-center">
          {safeValue.map(option => (
            <Chip
              key={Array.isArray(option.value) ? option.label : option.value}
              label={getChipLabel(option)}
              onRemove={() => handleRemove(option.value)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
